/**
 * @file 受験の学習スケジュールの設定を読み書きする hook。
 *
 * @what: 学習者ごとに1回読み、保存は PUT の結果で置き換える。設定が無い (404) のは正常系で `null`。
 * @why: 参考書ルートのタブの「スケジュール」で使う。draft を持たず、フォームの値は保存ボタンで送る。
 * @failure: 取得失敗は `error`、保存失敗は `saveError` に1行 (詳細は console のみ)。
 */

import { useCallback, useEffect, useState } from "react";
import {
  fetchExamPlan,
  putExamPlan,
  type StudyExamPlan,
  type StudyExamPlanInput,
} from "../api/examPlans";

export type ExamPlanState =
  | { status: "loading" }
  | { status: "ready"; plan: StudyExamPlan | null }
  | { status: "error" };

export function useExamPlan(familyId: string, personId: string | null, enabled: boolean) {
  const [state, setState] = useState<ExamPlanState>({ status: "loading" });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !personId) return;
    const controller = new AbortController();
    setState({ status: "loading" });
    fetchExamPlan(familyId, personId, controller.signal)
      .then((plan) => {
        if (!controller.signal.aborted) setState({ status: "ready", plan });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load exam plan", err);
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId, personId, enabled]);

  const save = useCallback(
    async (input: StudyExamPlanInput): Promise<boolean> => {
      if (!personId) return false;
      setSaving(true);
      setSaveError(null);
      try {
        const plan = await putExamPlan(familyId, personId, input);
        setState({ status: "ready", plan });
        return true;
      } catch (err) {
        console.error("[study] failed to save exam plan", err);
        setSaveError("保存できませんでした。時間を置いてもう一度お試しください。");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [familyId, personId],
  );

  return { state, save, saving, saveError };
}
